import React, { useState, useEffect } from "react";
import { ThinTextField, AddButton } from "../../common/GeneralComponent";
import { toast } from "react-toastify";
import { fetchData } from "../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Plus, Trash } from "lucide-react";

const CriticalStages = ({ 
  featureCategoryId, 
  onStagesSaved,
  isEditMode,
  showSubmitButton,
  showUpdateOnly,
  showFinalSubmit 
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [hasStages, setHasStages] = useState(false);
  const [stages, setStages] = useState([
    { id: null, stage: "", percentage: "" }
  ]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditMode && featureCategoryId) {
      fetchStages();
    }
  }, [featureCategoryId, isEditMode]);

  const fetchStages = async () => {
    try {
      const response = await fetchData(
        `critical-stages/${featureCategoryId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "",
        dispatch
      );
      
      if (response?.data && response.data.length > 0) {
        setHasStages(true);
        setStages(response.data.map(stage => ({
          id: stage.feature_category_critical_stages_id,
          stage: stage.stage_name || "",
          percentage: stage.percentage ? stage.percentage.toString() : ""
        })));
      }
    } catch (error) {
      console.error("Error fetching stages:", error);
    }
  };

  const handleAddStage = () => {
    setStages([...stages, { id: null, stage: "", percentage: "" }]);
  };

  const handleRemoveStage = (index) => {
    if (stages.length > 1) {
      setStages(stages.filter((_, i) => i !== index));
    }
  };

  const handleStageChange = (index, field, value) => {
    const updatedStages = [...stages];
    
    if (field === "percentage") {
      // Only allow numbers
      const numValue = value.replace(/[^0-9]/g, '');
      if (numValue === '' || (parseInt(numValue) >= 0 && parseInt(numValue) <= 100)) {
        updatedStages[index][field] = numValue;
      }
    } else {
      updatedStages[index][field] = value;
    }
    
    setStages(updatedStages);
  };

  const calculateTotalPercentage = () => {
    return stages.reduce((total, stage) => {
      return total + (parseInt(stage.percentage) || 0);
    }, 0);
  };

  const validateStages = () => {
    if (!hasStages) {
      return true; // Skip validation if no stages
    }

    // Check if all stages have required fields
    for (let i = 0; i < stages.length; i++) {
      if (!stages[i].stage.trim()) {
        toast.error(`Stage name is required for stage ${i + 1}`);
        return false;
      }
      if (!stages[i].percentage) {
        toast.error(`Percentage is required for stage ${i + 1}`);
        return false;
      }
    }

    // Check if total percentage equals 100
    const total = calculateTotalPercentage();
    if (total !== 100) {
      toast.error(`Total percentage must equal 100%. Current total: ${total}%`);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateStages()) {
      return;
    }

    try {
      setLoading(true);
      
      if (hasStages) {
        // If editing, first delete existing stages
        if (isEditMode) {
          await fetchData(
            `critical-stages-delete/${featureCategoryId}`,
            "DELETE",
            null,
            {},
            {},
            navigate,
            "",
            dispatch
          );
        }

        // Save all stages
        for (const stage of stages) {
          const stageData = {
            feature_category_id: featureCategoryId,
            stage_name: stage.stage,
            percentage: parseInt(stage.percentage)
          };

          await fetchData(
            "critical-stage-create",
            "POST",
            stageData,
            {},
            {},
            navigate,
            "",
            dispatch
          );
        }
      }
      
      if (isEditMode) {
        toast.success("Critical stages updated successfully");
        fetchStages();
      } else {
        toast.success("Critical stages saved successfully");
        if (onStagesSaved) onStagesSaved(stages);
      }
    } catch (error) {
      console.error("Error saving stages:", error);
      toast.error("Failed to save critical stages");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Has Stages Toggle */}
      <div className="flex items-center space-x-3 mb-2">
        <label className="text-[0.9rem] text-[#565656] font-[InterLight]">
          Has Critical Stages
        </label>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={hasStages}
            onChange={(e) => setHasStages(e.target.checked)}
            className="sr-only peer"
          />
           <div
            className="w-11 h-6 
            bg-[#e2e8f0]
            peer-focus:outline-none 
            rounded-full 
            peer 
            peer-checked:after:translate-x-full 
            peer-checked:after:border-white 
            after:content-[''] 
            after:absolute 
            after:top-[2px] 
            after:left-[2px] 
            after:bg-white 
            after:rounded-full 
            after:h-5 
            after:w-5 
            after:transition-all 
            peer-checked:bg-[#137e81]
            border border-[#b0b0b0]"
          ></div>
        </label>
      </div>

      {hasStages && (
        <>
          {/* Total Percentage Display */}
          <div className="bg-gray-100 p-2 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Total Percentage:</span>
              <span className={`font-bold ${calculateTotalPercentage() === 100 ? 'text-green-600' : 'text-red-600'}`}>
                {calculateTotalPercentage()}%
              </span>
            </div>
            {calculateTotalPercentage() !== 100 && (
              <p className="text-red-500 text-sm mt-1">
                Percentage must equal 100% to submit
              </p>
            )}
          </div>

          {stages.map((stage, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-gray-700">Stage {index + 1}</h3>
                {stages.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveStage(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash size={20} />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Stage Name */}
                <div>
                  <ThinTextField
                    label="Stage Name"
                    value={stage.stage}
                    onChange={(e) => handleStageChange(index, "stage", e.target.value)}
                    placeholder="Enter stage name"
                  />
                </div>

                {/* Percentage */}
                <div>
                  <label className="text-[0.9rem] text-[#565656] font-[InterLight] block">
                    Percentage (%)
                  </label>
                  <input
                    type="text"
                    value={stage.percentage}
                    onChange={(e) => handleStageChange(index, "percentage", e.target.value)}
                    placeholder="Enter percentage (0-100)"
                    className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Add More Button */}
          <button
            type="button"
            onClick={handleAddStage}
            className="flex items-center space-x-2 text-[#137e81] hover:text-[#0f6366]"
          >
            <Plus size={20} />
            <span>Add More Stage</span>
          </button>
        </>
      )}

      {/* Buttons */}
      <div className="pt-3 flex justify-end">
        {showUpdateOnly && (
          <AddButton 
            text="Update" 
            type="button"
            onClick={handleSubmit}
            disabled={loading}
          />
        )}
        {(showSubmitButton || showFinalSubmit) && !isEditMode && (
          <AddButton 
            text="Submit" 
            type="button"
            onClick={handleSubmit}
            disabled={loading || (hasStages && calculateTotalPercentage() !== 100)}
          />
        )}
      </div>
    </div>
  );
};

export default CriticalStages;